import { baseApi } from './baseApi'
import { Payer, PayerCrossoverIdDto } from 'store/types'
import {
  CreateDmePayerDto,
  DmePayerMergeOption,
  MergeDmePayersDto,
  UpdateDmePayerDto,
} from 'ts-back-end/src/business/dme-payer/types/dme-payer'
import {
  CreatePayer,
  PatientOrderPayerItem,
  PayerSearchItem,
  UpdatePayerDto,
  UpdatePayerNoteDto,
} from 'ts-back-end/src/business/payer/dto/payer.dto'
import { PayersListQuery } from 'ts-back-end/src/business/payer/queries/get-payers-list.query'
import { PayersList } from 'ts-back-end/src/business/payer/dto/payers-list'
import {
  OptumPayerIdCheck,
  OptumPayerIdCheckQuery,
} from 'ts-back-end/src/business/insurance-verification/types/types'

type DmePayorListRequest = {
  officeIds?: number
  size: number
  name?: string
  status?: 'assigned' | 'unassigned'
  page?: number
  sortField?: string
  sortOrder?: 'ASC' | 'DESC'
}

export const payerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPayers: builder.query<Payer[], { [key: string]: string }>({
      query: (query) => ({ url: `/dme/dme-payers`, params: query }),
    }),
    createPayer: builder.mutation<Payer, CreateDmePayerDto>({
      query: (data) => ({
        method: `POST`,
        url: `/dme/dme-payers`,
        body: data,
      }),
    }),
    modifyPayer: builder.mutation<Payer, UpdateDmePayerDto>({
      query: (data) => ({
        method: `PATCH`,
        url: `/dme/dme-payers/${data.id}`,
        body: data,
      }),
    }),
    deletePayer: builder.mutation<Payer, { id: string }>({
      query: ({ id }) => ({
        method: `DELETE`,
        url: `/dme/dme-payers/${id}`,
      }),
    }),
    linkDmePayerToLabPayer: builder.mutation<
      Payer,
      { orderId: number; dmePayerId: number; labPayerId: number }
    >({
      query: ({ dmePayerId, labPayerId }) => ({
        method: `PUT`,
        url: `/lab/dme-payers/${dmePayerId}/link`,
        body: { labPayerId, dmePayerId },
      }),
      invalidatesTags: (result, error, { orderId: id, labPayerId }) => [
        { type: 'Order', id },
        { type: 'LabDmePayers', labPayerId },
      ],
    }),
    labCreateDmePayer: builder.mutation<Payer, CreateDmePayerDto>({
      query: (data) => ({
        method: `POST`,
        url: `/lab/dme-payers`,
        body: data,
      }),
    }),
    labModifyPayer: builder.mutation<Payer, UpdateDmePayerDto>({
      query: (data) => ({
        method: `PATCH`,
        url: `/lab/dme-payers/${data.id}`,
        body: data,
      }),
    }),
    mergeDmePayers: builder.mutation<void, MergeDmePayersDto>({
      query: (data) => ({
        method: `POST`,
        url: `/lab/dme-payers/merge`,
        body: data,
      }),
      invalidatesTags: ['LabDmePayers', 'Order', 'OrderList', 'OrderHistory'],
    }),
    getLabPayers: builder.query<PayerSearchItem[], { active?: boolean; name?: string; patientId?: number }>({
      query: (query) => ({ url: `/lab/payers`, params: query }),
      providesTags: ['LabPayer'],
    }),
    getLabPayerList: builder.query<PayersList, PayersListQuery>({
      query: (query) => ({ url: `/lab/payers/list`, params: query }),
      providesTags: ['LabPayerList'],
    }),
    getLabPayer: builder.query<Payer, { id: string }>({
      query: ({ id }) => ({ url: `/lab/payers/${id}` }),
      providesTags: (result, error, { id }) => [{ type: 'LabPayer', id }],
    }),
    getLabPayerDmePayers: builder.query<Payer[], { id: string }>({
      query: ({ id }) => ({ url: `/lab/payers/${id}/dme-payers` }),
      providesTags: (result, error, { id }) => [{ type: 'LabDmePayers', id }],
    }),
    getLabPayerHistory: builder.query<Payer[], { id: string }>({
      query: ({ id }) => ({ url: `/lab/payers/${id}/history` }),
      providesTags: (result, error, { id }) => [{ type: 'PayerHistory', id }],
    }),
    getLabAllDmePayers: builder.query<Payer[], DmePayorListRequest>({
      query: (query) => ({ url: `/lab/dme-payers/list`, params: query }),
    }),
		getLabDmePayerMergeOptions: builder.query<DmePayerMergeOption[], { officeId: number }>({
			query: ({ officeId }) => ({ url: `/lab/dme-payers/merge-options`, params: { officeId } }),
		}),
		getLabPatientOrderPayers: builder.query<PatientOrderPayerItem[], { patientId: number }>({
			query: ({ patientId }) => ({ url: `/lab/payers/${patientId}/orders` }),
		}),
    createLabPayer: builder.mutation<Payer, CreatePayer>({
      query: (data) => ({
        method: `POST`,
        url: `/lab/payers`,
        body: data,
      }),
      invalidatesTags: ['LabPayer', 'LabPayerList'],
    }),
    updateLabPayer: builder.mutation<Payer, UpdatePayerDto>({
      query: (data) => ({
        method: `PATCH`,
        url: `/lab/payers/${data.id}`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'LabPayer', id },
        { type: 'PayerHistory', id },
        { type: 'LabPayerList' },
      ],
    }),
    addLabPayerName: builder.mutation<Payer, { payerId: number; name: string }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/payers/${params.payerId}/names`,
        body: params,
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', payerId }],
    }),
    removeLabPayerName: builder.mutation<Payer, { payerId: number; uuid: number }>({
      query: (params) => ({
        method: `DELETE`,
        url: `/lab/payers/${params.payerId}/names/${params.uuid}`,
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', payerId }],
    }),
    checkLabPayerOptumIds: builder.query<OptumPayerIdCheck, OptumPayerIdCheckQuery>({
      query: (params) => ({ url: `/lab/payers/optum-check`, params }),
    }),
    // The ids other payers forward claims to this payer under (835 NM1*TT).
    getLabPayerCrossoverIds: builder.query<PayerCrossoverIdDto[], number>({
      query: (payerId) => ({ url: `/lab/payers/${payerId}/crossover-ids` }),
      providesTags: (result, error, payerId) => [{ type: 'LabPayer', id: `crossover-${payerId}` }],
    }),
    removeLabPayerCrossoverId: builder.mutation<void, { payerId: number; id: string }>({
      query: ({ payerId, id }) => ({
        method: `DELETE`,
        url: `/lab/payers/${payerId}/crossover-ids/${id}`,
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', id: `crossover-${payerId}` }],
    }),
    addLabPayerNote: builder.mutation<void, { payerId: number; note: string; type?: string }>({
      query: ({ payerId, note, type }) => ({
        method: `POST`,
        url: `/lab/payers/${payerId}/notes`,
        body: { payerId, note, display: true, type },
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', id: String(payerId) }],
    }),
    updateLabPayerNote: builder.mutation<void, { payerId: number; noteUuid: string } & Pick<UpdatePayerNoteDto, 'note'>>({
      query: ({ payerId, noteUuid, note }) => ({
        method: `PATCH`,
        url: `/lab/payers/${payerId}/notes/${noteUuid}`,
        body: { note },
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', id: String(payerId) }],
    }),
    removeLabPayerNote: builder.mutation<void, { payerId: number; noteUuid: string }>({
      query: ({ payerId, noteUuid }) => ({
        method: `DELETE`,
        url: `/lab/payers/${payerId}/notes/${noteUuid}`,
      }),
      invalidatesTags: (result, error, { payerId }) => [{ type: 'LabPayer', id: String(payerId) }],
    }),
  }),
})

export const {
  useGetPayersQuery,
  useCreatePayerMutation,
  useModifyPayerMutation,
  useDeletePayerMutation,
  useLinkDmePayerToLabPayerMutation,
  useMergeDmePayersMutation,
  useGetLabPayersQuery,
  useGetLabPayerListQuery,
  useGetLabPayerDmePayersQuery,
  useGetLabPayerHistoryQuery,
  useGetLabAllDmePayersQuery,
  useGetLabDmePayerMergeOptionsQuery,
  useLabCreateDmePayerMutation,
  useLabModifyPayerMutation,
  useGetLabPayerQuery,
  useLazyGetLabPayerQuery,
  useCreateLabPayerMutation,
  useUpdateLabPayerMutation,
  useAddLabPayerNameMutation,
  useRemoveLabPayerNameMutation,
  useGetLabPayerCrossoverIdsQuery,
  useLazyCheckLabPayerOptumIdsQuery,
  useRemoveLabPayerCrossoverIdMutation,
  useGetLabPatientOrderPayersQuery,
  useAddLabPayerNoteMutation,
  useUpdateLabPayerNoteMutation,
  useRemoveLabPayerNoteMutation,
} = payerApi
